import React from "react";
import "../styles/CollectorMap.css";

/**
 * MapStatusSurface — every non-OK state the map can truthfully be in.
 *
 * Two variants, one component, because the choice between them is a fact about
 * the map rather than a style:
 *
 *   card    — there is nothing truthful to show on the map (no pins). The
 *             message is the content, so it takes the middle of the screen.
 *   banner  — the pins on screen are still valid, they are just incomplete or
 *             stale. A centred card would cover the very thing it is
 *             describing, so this sits in the top slot instead.
 *
 * This component owns no copy and no state machine. `emptyMapReason` and
 * `mapCopy` still decide which words appear; only their presentation lives
 * here. That is what keeps "rate limited" from ever drifting into sounding like
 * "nobody is nearby".
 */
const ACTION_BASE = {
  border: "none",
  borderRadius: 10,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "Inter, sans-serif",
  minHeight: 44,
  padding: "0 18px",
};

const StatusAction = ({ action, tone }) => {
  if (!action || !action.label) return null;
  const primary = tone === "primary";
  return (
    <button
      type="button"
      data-testid={action.testId}
      onClick={action.onClick}
      style={{
        ...ACTION_BASE,
        fontSize: 12.5,
        background: primary ? "#6d28d9" : "#fff",
        color: primary ? "#fff" : "#334155",
        border: primary ? "none" : "1px solid rgba(15,23,42,0.12)",
      }}
    >
      {action.label}
    </button>
  );
};

const MapStatusSurface = ({
  variant = "card",
  title,
  body,
  caption,
  spinner = false,
  iconPath = null,
  primaryAction = null,
  secondaryAction = null,
  // Card variant only. `[{ key, label, collectors, onSelect }]` — where
  // collectors actually are, when there are none where the user is standing.
  // The card branch is reached precisely when the map has nothing to show, so
  // this is the one place a destination can be offered without competing with
  // real pins.
  hotspots = null,
  top,
  transition,
  testId = "map-status-surface",
  // The discovery-state suite reads the headline by its own testid, and that
  // contract is about the copy being right rather than about which component
  // draws it — so the caller keeps naming it.
  titleTestId = "map-status-title",
}) => {
  if (!title && !body) return null;

  if (variant === "banner") {
    return (
      <div
        data-testid={testId}
        data-variant="banner"
        role="status"
        className="collector-fade-enter"
        style={{
          position: "absolute",
          top,
          transition,
          left: 12,
          right: 12,
          zIndex: 12,
          padding: "11px 12px",
          borderRadius: 14,
          background: "rgba(255,255,255,0.97)",
          border: "1px solid rgba(15,23,42,0.09)",
          boxShadow: "0 6px 18px rgba(15,23,42,0.12)",
          fontFamily: "Inter, sans-serif",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>
          <span
            aria-hidden="true"
            style={{
              flexShrink: 0,
              width: 20,
              height: 20,
              borderRadius: "50%",
              background: "#fef3c7",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginTop: 1,
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="#b45309" aria-hidden="true">
              <path d="M12 2L1 21h22L12 2zm1 14h-2v2h2v-2zm0-6h-2v4h2v-4z" />
            </svg>
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span
              data-testid={titleTestId}
              style={{ display: "block", fontSize: 12.5, fontWeight: 700, color: "#0f172a", lineHeight: 1.3 }}
            >
              {title}
            </span>
            {body && (
              <span
                style={{
                  display: "block",
                  fontSize: 11.5,
                  fontWeight: 500,
                  color: "#64748b",
                  lineHeight: 1.4,
                  marginTop: 2,
                }}
              >
                {body}
              </span>
            )}
          </span>
          {primaryAction && (
            <button
              type="button"
              data-testid={primaryAction.testId}
              onClick={primaryAction.onClick}
              style={{
                flexShrink: 0,
                minHeight: 44,
                padding: "0 12px",
                borderRadius: 10,
                border: "1px solid rgba(15,23,42,0.12)",
                color: "#0f172a",
                fontSize: 11.5,
                fontWeight: 700,
                background: "#fff",
                cursor: "pointer",
                fontFamily: "Inter, sans-serif",
              }}
            >
              {primaryAction.label}
            </button>
          )}
          {/*
            The banner used to render its primary action only, and that was a
            real gap rather than a stylistic one: this banner OUTRANKS the
            roaming bar for the map's single top slot, and that bar is the only
            place "Back to my location" is drawn. A failed refresh while roaming
            therefore removed every way out of a roam. A banner that displaces a
            surface has to be able to carry that surface's escape hatch.
          */}
          {secondaryAction && (
            <button
              type="button"
              data-testid={secondaryAction.testId}
              onClick={secondaryAction.onClick}
              style={{
                flexShrink: 0,
                minHeight: 44,
                padding: "0 12px",
                borderRadius: 10,
                border: "1px solid rgba(15,23,42,0.12)",
                color: "#334155",
                fontSize: 11.5,
                fontWeight: 700,
                background: "transparent",
                cursor: "pointer",
                fontFamily: "Inter, sans-serif",
              }}
            >
              {secondaryAction.label}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={testId}
      data-variant="card"
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 12,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 22px",
        // The card is the only interactive thing in this layer; the rest of the
        // layer must not swallow taps meant for the map underneath.
        pointerEvents: "none",
        fontFamily: "Inter, sans-serif",
      }}
    >
      <div
        role="status"
        className="collector-fade-enter"
        style={{
          width: "100%",
          maxWidth: 306,
          padding: "22px 20px",
          borderRadius: 20,
          background: "#fff",
          border: "1px solid rgba(15,23,42,0.07)",
          boxShadow: "0 18px 44px rgba(15,23,42,0.16)",
          textAlign: "center",
          marginBottom: 60,
          pointerEvents: "auto",
        }}
      >
        {spinner && <span className="collector-status-spinner" aria-hidden="true" />}
        {iconPath && (
          <span
            aria-hidden="true"
            style={{
              display: "flex",
              width: 40,
              height: 40,
              margin: "0 auto 12px",
              borderRadius: 13,
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(109,40,217,0.08)",
              color: "#6d28d9",
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
            </svg>
          </span>
        )}
        <div
          data-testid={titleTestId}
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: "#0f172a",
            letterSpacing: "-0.01em",
            marginBottom: 6,
          }}
        >
          {title}
        </div>
        {body && (
          <div style={{ fontSize: 12.5, fontWeight: 500, lineHeight: 1.5, color: "#64748b" }}>{body}</div>
        )}
        {caption && (
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 500,
              lineHeight: 1.45,
              color: "#64748b",
              marginTop: 8,
            }}
          >
            {caption}
          </div>
        )}
        {Array.isArray(hotspots) && hotspots.length > 0 && (
          <div
            data-testid="map-hotspot-list"
            style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 6 }}
          >
            {hotspots.map((spot) => (
              <button
                key={spot.key}
                type="button"
                data-testid="map-hotspot-row"
                onClick={spot.onSelect}
                style={{
                  ...ACTION_BASE,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                  fontSize: 12.5,
                  padding: "0 12px",
                  background: "#f8fafc",
                  color: "#0f172a",
                  border: "1px solid rgba(15,23,42,0.08)",
                  textAlign: "left",
                }}
              >
                <span style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {spot.label}
                </span>
                {/* Singular/plural matters here: the floor is 3, but that is a
                    constant on the server and this copy must not assume it. */}
                <span style={{ flexShrink: 0, fontWeight: 500, color: "#64748b" }}>
                  {spot.collectors} {spot.collectors === 1 ? "collector" : "collectors"}
                </span>
              </button>
            ))}
          </div>
        )}
        {(primaryAction || secondaryAction) && (
          <div style={{ marginTop: 14, display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
            <StatusAction action={primaryAction} tone="primary" />
            <StatusAction action={secondaryAction} tone="secondary" />
          </div>
        )}
      </div>
    </div>
  );
};

export default MapStatusSurface;
